/**
 * TAAR — component-product-card.js
 * Custom element for product card: wishlist, quick-add, hover-image preload.
 * Loaded globally via theme.liquid. Depends on window.TAAR (taar-global.js).
 */

(function () {
  'use strict';

  class TaarProductCard extends HTMLElement {
    connectedCallback() {
      // Defer until TAAR is available (defer scripts run before DOMContentLoaded)
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      } else {
        this._init();
      }
    }

    disconnectedCallback() {
      if (this._unsubscribers) {
        this._unsubscribers.forEach(function (fn) { fn(); });
      }
    }

    _init() {
      if (!window.TAAR) return;
      this._unsubscribers = [];
      this._initWishlist();
      this._initQuickAdd();
      this._initHoverImage();
    }

    /* ── Wishlist ─────────────────────────────────────────────────────────── */

    _initWishlist() {
      var btn = this.querySelector('[data-wishlist-btn]');
      if (!btn) return;

      var productId = parseInt(this.dataset.productId, 10);
      this._syncWishlistBtn(btn, productId);

      var self = this;

      var onAdded   = function () { self._syncWishlistBtn(btn, productId); };
      var onRemoved = function () { self._syncWishlistBtn(btn, productId); };

      TAAR.EventBus.on('wishlist:added',   onAdded);
      TAAR.EventBus.on('wishlist:removed', onRemoved);

      this._unsubscribers.push(
        function () { TAAR.EventBus.off('wishlist:added',   onAdded); },
        function () { TAAR.EventBus.off('wishlist:removed', onRemoved); }
      );

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        TAAR.Wishlist.toggle({
          id:        productId,
          variantId: parseInt(self.dataset.variantId, 10),
          handle:    self.dataset.handle,
          title:     self.dataset.productTitle,
          price:     parseInt(self.dataset.price, 10),
          image:     self.dataset.image || '',
          url:       self.dataset.url   || '',
          requiresOptions: self.dataset.multipleVariants === 'true'
        });
      });
    }

    _syncWishlistBtn(btn, productId) {
      var active  = TAAR.Wishlist.has(productId);
      var label   = btn.querySelector('[data-wishlist-label]');

      btn.setAttribute('aria-pressed', active.toString());
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-label', active
        ? (btn.dataset.labelActive   || 'Remove from wishlist')
        : (btn.dataset.labelInactive || 'Add to wishlist')
      );

      if (label) {
        label.textContent = active
          ? (btn.dataset.labelActive   || 'Remove from wishlist')
          : (btn.dataset.labelInactive || 'Add to wishlist');
      }
    }

    /* ── Quick add ────────────────────────────────────────────────────────── */

    _initQuickAdd() {
      var btn = this.querySelector('[data-quick-add-btn]');
      if (!btn || btn.tagName === 'A') return; // multi-variant: <a> handles navigation

      var variantId = parseInt(this.dataset.variantId, 10);

      btn.addEventListener('click', async function (e) {
        e.preventDefault();
        if (btn.disabled || btn.classList.contains('btn--loading')) return;

        btn.classList.add('btn--loading');
        btn.disabled = true;

        try {
          await TAAR.Cart.addItem(variantId, 1);
          TAAR.EventBus.emit('cart:opened');
        } catch (err) {
          console.error('[TAAR] Quick add failed:', err);
        } finally {
          btn.classList.remove('btn--loading');
          btn.disabled = false;
        }
      });
    }

    /* ── Hover image preload ──────────────────────────────────────────────── */

    _initHoverImage() {
      // Hover image already has src (lazy via browser). Nothing to do.
      // This hook reserved for future canvas-swap patterns.
    }
  }

  if (!customElements.get('taar-product-card')) {
    customElements.define('taar-product-card', TaarProductCard);
  }

}());
